import type { Category, Menu, Product } from "../../api/types";

/** Category colours as in the prototype, in menu order, unless the menu sets its own. */
const PALETTE = [
  "#C8541C",
  "#C9A227",
  "#A33B5A",
  "#3E7C9A",
  "#5B6FA8",
  "#9A4B2B",
  "#6E8B3D",
  "#0E6A5A",
  "#2F6FA3",
  "#7B4B94",
  "#C8741C",
  "#8A6D3B",
  "#B0892A",
  "#4E8F3A",
  "#4F6D7A",
  "#8A7F72",
  "#B03A5B",
];

export function categoryColor(category: Category, index: number): string {
  return category.color ?? PALETTE[index % PALETTE.length]!;
}

/** Categories that have something to sell, in menu order. */
export function sellableCategories(menu: Menu): Category[] {
  const used = new Set(Object.values(menu.products).map((product) => product.categoryId));
  return menu.categories.filter((category) => used.has(category.id));
}

const BY_NAME = new Intl.Collator("nl", { sensitivity: "base", numeric: true });

/**
 * The products of a category as they are shown everywhere: on the tiles, in the Tafo app and in every
 * list. In the shop's own order when it made one in Menu & prijzen (Furkan, 03/10/2026). Else the
 * specialties first, in the order of the menu (Stel zelf je pizza samen, Splitza), then everything else
 * alphabetically (Furkan, 01/10/2026). A product the shop's order does not name yet, a new one, comes
 * after the others, where the alphabet puts it among those.
 */
export function productsIn(menu: Menu, categoryId: string): Product[] {
  const products = Object.values(menu.products).filter((product) => product.categoryId === categoryId);
  const byAlphabet = [
    ...products.filter((product) => product.special === true),
    ...products.filter((product) => product.special !== true).sort((a, b) => BY_NAME.compare(a.name, b.name)),
  ];
  const order = menu.categories.find((category) => category.id === categoryId)?.productOrder;
  if (order === undefined) return byAlphabet;
  const places = new Map(order.map((id, place) => [id, place]));
  const placeOf = (product: Product) => places.get(product.id) ?? order.length;
  // a stable sort: what the order does not name keeps the alphabet
  return byAlphabet.sort((a, b) => placeOf(a) - placeOf(b));
}
